/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useMemo, useState } from 'react';

import type { Filter } from '@coursera-web/cds-labs';
import { CollectionsModule } from '@coursera-web/cds-labs';

import { Button, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import CarouselCollectionType from 'bundles/unified-description-page-common/components/recommendations/CarouselCollectionType';
import { toCarouselCollection } from 'bundles/unified-description-page-common/components/recommendations/toCarouselCollection';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import type { CollectionEntityFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/CollectionEntityFragment';
import { useStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';
import { previewXdpAsADiscoveryToolVariant } from 'bundles/unified-description-page-common/utils/xdpAsADiscoveryToolUtils';

// eslint-disable-next-line @coursera-web/custom-rules/i18n-import-rule
import _t from 'i18n!nls/xdp';

type CourseCollections = {
  recommendationsCollection?: CollectionEntityFragment[] | null;
  relatedCoursesCollection?: CollectionEntityFragment[] | null;
  degreesCollection?: CarouselCollection | null;
};

type Props = CourseCollections & {
  domainName?: string;
  anchorId?: string;
};

const ExploreMoreCourseRecommendations = ({
  recommendationsCollection,
  relatedCoursesCollection,
  degreesCollection,
  domainName,
  anchorId,
}: Props) => {
  const trackV3Event = useTracker();
  const courseraPlusData = useCourseraPlusOwnership();
  const isStreamlinedXDP = useStreamlinedXDP();
  const experimentVariant = previewXdpAsADiscoveryToolVariant();
  const [currentFilterIndex, setCurrentFilterIndex] = useState(0);
  const { subfolderLocaleCode } = usePageInfo();
  const shouldDisplayCourseraPlusVariant = courseraPlusData?.isCourseraPlusEligibleProduct && experimentVariant === 'B';

  const collectionsForModule = useMemo(() => {
    const collections: CarouselCollection[] = [];
    const filters: Filter[] = [];

    if (recommendationsCollection) {
      collections.push(
        toCarouselCollection({
          recommendationsCollection,
          collectionId: CarouselCollectionType.RECOMMENDATIONS,
          subfolderLocaleCode,
        })
      );
      filters.push({
        filterName: _t('Recommended'),
        id: 'recommended',
      });

      const proCertRecs = recommendationsCollection.filter((rec) => rec.productType === 'PROFESSIONAL_CERTIFICATE');
      if (!isStreamlinedXDP && proCertRecs.length) {
        collections.push(
          toCarouselCollection({
            recommendationsCollection: proCertRecs,
            collectionId: `${CarouselCollectionType.RECOMMENDATIONS}_professional_certificates`,
            subfolderLocaleCode,
          })
        );
        filters.push({
          filterName: _t('Professional Certificates'),
          id: 'professionalcertificates',
        });
      }

      const s12nRecs = recommendationsCollection.filter((rec) => rec.productType === 'SPECIALIZATION');
      if (!isStreamlinedXDP && s12nRecs.length) {
        collections.push(
          toCarouselCollection({
            recommendationsCollection: s12nRecs,
            collectionId: `${CarouselCollectionType.RECOMMENDATIONS}_specializations`,
            subfolderLocaleCode,
          })
        );
        filters.push({
          filterName: _t('Specializations'),
          id: 'specializations',
        });
      }
    }

    if (relatedCoursesCollection) {
      collections.push(
        toCarouselCollection({
          recommendationsCollection: relatedCoursesCollection,
          collectionId: CarouselCollectionType.RELATED_COURSE,
          subfolderLocaleCode,
        })
      );
      filters.push({
        filterName: isStreamlinedXDP ? _t('Related courses') : _t('Related'),
        id: 'related',
      });
    }

    if (isStreamlinedXDP && degreesCollection) {
      collections.push(degreesCollection);
      filters.push({
        filterName: _t('Degrees'),
        id: 'degrees',
      });
    }

    return {
      collections,
      filters,
    };
  }, [recommendationsCollection, relatedCoursesCollection, subfolderLocaleCode, degreesCollection, isStreamlinedXDP]);

  const handleFilterChange = useCallback<(index: number) => void>(
    (index: number) => {
      setCurrentFilterIndex(index);
      trackV3Event('click_button', {
        button: {
          name: 'collections_filter_chip',
          linkName: collectionsForModule.filters[index].filterName,
        },
      });
    },
    [collectionsForModule.filters, trackV3Event]
  );

  const title = useMemo(() => {
    if (domainName && shouldDisplayCourseraPlusVariant) {
      return _t('Dive into endless #{domainName} content with one subscription', { domainName });
    }
    if (domainName && isStreamlinedXDP) {
      return _t("Recommended if you're interested in #{domainName}", { domainName });
    }
    if (domainName && !shouldDisplayCourseraPlusVariant) {
      return _t('Explore more from #{domainName}', { domainName });
    }
    return _t('Explore more');
  }, [domainName, shouldDisplayCourseraPlusVariant, isStreamlinedXDP]);

  if (!recommendationsCollection?.length && !relatedCoursesCollection?.length && !degreesCollection) {
    return null;
  }

  const carouselCollection: CarouselCollection = collectionsForModule.collections[currentFilterIndex];

  if (shouldDisplayCourseraPlusVariant) {
    carouselCollection.renderCustomPrimaryCta = ({ size, fullWidth, variantOverride }) => (
      <Button
        size={size}
        fullWidth={fullWidth}
        component="a"
        variant={variantOverride ?? 'primary'}
        onClick={() => {
          trackV3Event('click_button', {
            button: {
              name: 'c_plus_learn_more',
            },
          });
        }}
        href="/courseraplus"
        css={
          fullWidth
            ? undefined
            : css`
                ${breakpoints.down('xs')} {
                  margin-right: var(--cds-spacing-200);
                }
              `
        }
      >
        {_t('Try for Free')}
      </Button>
    );
  }

  return (
    <TrackedDiv
      trackingName="recommendations_module"
      trackClicks={false}
      requireFullyVisible={true}
      withVisibilityTracking={true}
    >
      <Section anchorId={anchorId}>
        <CollectionsModule
          headline={title}
          anchorId={anchorId}
          filters={collectionsForModule.filters}
          onFilterChange={handleFilterChange}
          currentCollectionSetIndex={currentFilterIndex}
          collections={[collectionsForModule.collections[currentFilterIndex]]}
          layout="text-top/image-bottom"
          removeTopPadding
          removeBottomPadding
        />
      </Section>
    </TrackedDiv>
  );
};

export default ExploreMoreCourseRecommendations;
